import isEmpty from "lodash/isEmpty";
import {inject, observer} from "mobx-react";
import {DragDropContext} from "react-dnd";
import withScrolling from "react-dnd-scrollzone";
import HTML5Backend from "react-dnd-html5-backend";
import React from "react";
import styled from "styled-components";
import DNDReelClip from "components/DNDReelClip";
import TrimPlayDialog from "components/TrimPlayDialog";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import CircularProgress from "@material-ui/core/CircularProgress";
import {DEFAULT_UNSET_WATERMARK} from "../httpClients/ClippyApi";

const Container = styled.div`
  grid-area: reel;
  overflow-x: hidden;
`;

const ReelClipContainer = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  width: inherit;
  padding: 10px 0;
`;

const ReelOptionsContainer = styled.div`
  float: left;
`;

const ReelActionsContainer = styled.div`
  float: right;
`;

const ReelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  margin-bottom: 1.5rem;
`;

const ClipDuration = styled.div`
  display: inline-flex;
  margin-left: 20px;
  margin-right: 20px;
`;

const Label = styled.div`
  padding: 2px 0 0 15px;
`;

const Error = styled.div`
  color: #d50032;
  margin: 30px 0 0 30px;
`;

// HOC to help with DND scrolling.
const ScrollingContainer = withScrolling(ReelClipContainer);

class ReelEditor extends React.Component {
  render() {
    const {reelStore, trimPlayDialogStore, watermarkStore} = this.props;

    // Watermarks returned as filenames  or "None" (default)
    // Helper method to format for readability (e.g. "xyz.png" -> "XYZ")
    const formatWatermarkName = (name) => name !== DEFAULT_UNSET_WATERMARK
      ? name.split(".")[0].toUpperCase()
      : name;

    return (
      <Dialog
        open={reelStore.isDialogOpen}
        onClose={() => reelStore.setDialogOpen(false)}
        fullWidth
        maxWidth={"lg"}
      >
        <DialogTitle>
          <TextField
            autoFocus
            margin="dense"
            id="clip-title"
            label="Clip Title"
            fullWidth
            helperText={reelStore.titleError ? "Title is required" : ""}
            value={reelStore.clipTitle}
            onChange={e => reelStore.setClipTitle(e.target.value)}
            required
            error={reelStore.titleError}
          />
        </DialogTitle>
        <DialogContent style={{textAlign: "center"}}>
          {reelStore.reelClips.length === 0 ? (
            reelStore.fetchingClips ? (
              <CircularProgress/>
            ) : (
              <p style={{marginBottom: "1.5rem"}}>
                No clips selected for reel
              </p>
            )
          ) : (
            <Container>
              {!isEmpty(reelStore.errorMessage) ? (
                <Error>{reelStore.errorMessage}</Error>
              ) : null}
              <ReelWrapper>
                <ScrollingContainer>
                  {reelStore.reelClips.map((clip, i) => {
                    return (
                      <DNDReelClip
                        key={clip.url}
                        index={i}
                        clip={clip}
                        removeHandler={id => reelStore.removeClip(id)}
                        openTrimPlayDialog={() =>
                          trimPlayDialogStore.open(clip, i)
                        }
                        handleVideoMetadata={video =>
                          reelStore.addClipMetadata(video)
                        }
                        moveClip={(startIndex, endIndex) =>
                          reelStore.moveClip(startIndex, endIndex)
                        }
                      />
                    );
                  })}
                </ScrollingContainer>
              </ReelWrapper>
              <TrimPlayDialog/>
            </Container>
          )}
        </DialogContent>
        <DialogActions style={{display: "inline-block"}}>
          <ReelOptionsContainer>
            <FormControlLabel
              control={
                <Switch
                  checked={reelStore.applyCrossfade}
                  onChange={e => (reelStore.applyCrossfade = e.target.checked)}
                  value="crossfade"
                />
              }
              label="Apply Crossfade"
            />
            <FormControl style={{minWidth: "7rem", marginLeft: "1rem"}}>
              <InputLabel id="crop-label">Crop Ratio</InputLabel>
              <Select
                variant="standard"
                labelId="crop-label"
                label="Crop Ratio"
                value={reelStore.cropRatio}
                onChange={e => reelStore.setCropRatio(e.target.value)}
              >
                {reelStore.cropRatios.map((c, i) => (
                  <MenuItem key={i} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{minWidth: "7rem", marginLeft: "1rem"}}>
              <InputLabel id="watermark">Watermark</InputLabel>
              <Select
                variant="standard"
                labelId="watermark"
                label="Watermark"
                value={reelStore.watermark}
                onChange={e => reelStore.setWatermark(e.target.value)}
              >
                {watermarkStore.watermarks.map((c, i) => (
                  <MenuItem key={i} value={c}>
                    {formatWatermarkName(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ReelOptionsContainer>
          <ReelActionsContainer>
            <ClipDuration>
              <Label>{`Total Time: ${reelStore.reelDurationStr}`}</Label>
            </ClipDuration>
            <Button
              variant="contained"
              color="primary"
              style={{marginRight: "20px"}}
              onClick={() => reelStore.createClip()}
              disabled={reelStore.reelClips.length === 0}
            >
              Create
            </Button>
            <Button onClick={() => reelStore.setDialogOpen(false)}>
              Cancel
            </Button>
          </ReelActionsContainer>
        </DialogActions>
      </Dialog>
    );
  }
}

export default DragDropContext(HTML5Backend)(
  inject("reelStore", "trimPlayDialogStore", "watermarkStore")(observer(ReelEditor))
);



// WEBPACK FOOTER //
// ./src/pages/ReelEditor.js